import Loader from '../components/Loader';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import React, { useEffect, useState } from 'react';
import feather from 'feather-icons';
import SuccessToast from '../toast/SuccessToast';
import Alert from '../components/Alert';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import { useLanguage } from '../context/LanguageContext';
import { FiEye, FiEyeOff, FiLock, FiShield } from 'react-icons/fi';
import { getPageMeta } from '../util/seoHelper';
import '../styles/PasswordUpdatePage.css';

function PasswordUpdate() {
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState('');

    const refreshSignal = useOutletRefreshSignal();
    const navigate = useNavigate();
    const { t, language } = useLanguage();

    // Check password strength
    const checkPasswordStrength = (password) => {
        if (!password) {
            setPasswordStrength('');
            return;
        }

        if (password.length < 6) {
            setPasswordStrength('weak');
        } else if (password.length < 8) {
            setPasswordStrength('medium');
        } else if (
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password) &&
            /[!@#$%^&*]/.test(password)
        ) {
            setPasswordStrength('strong');
        } else {
            setPasswordStrength('medium');
        }
    };

    const handleNewPasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        checkPasswordStrength(value);
    };

    const handlePasswordUpdateSubmit = async (event) => {
        event.preventDefault();
        setError(null);

        // Validation
        if (!oldPassword.trim()) {
            setError('oldPasswordRequired');
            return;
        }
        if (!newPassword.trim()) {
            setError('newPasswordRequired');
            return;
        }
        if (!confirmPassword.trim()) {
            setError('confirmPasswordRequired');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('passwordsDoNotMatch');
            return;
        }
        if (oldPassword === newPassword) {
            setError('newPasswordMustBeDifferent');
            return;
        }
        if (newPassword.length < 6) {
            setError('passwordTooShort');
            return;
        }

        setLoading(true);

        const payload = {
            oldPassword,
            password: newPassword,
            confirmPassword,
        };

        try {
            const response = await serverClient.post('/api/user/update-password', payload);
            if (response.status === 201 || response.status === 200) {
                setShowToast(true);
                setOldPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setPasswordStrength('');

                setTimeout(() => {
                    navigate('/profile');
                }, 1500);
            }
        } catch (error) {
            if (error.response?.status === 401) {
                DeleteToken();
                setError('userNotAuthorized');
            } else if (error.response?.status === 400) {
                setError(error.response?.data?.message || 'invalidOldPassword');
            } else {
                setError('somethingWentWrong');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        feather.replace();
    }, []);

    useEffect(() => {
        if (refreshSignal) {
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setError(null);
            setShowToast(false);
            setPasswordStrength('');
            setShowOldPassword(false);
            setShowNewPassword(false);
            setShowConfirmPassword(false);
        }
    }, [refreshSignal]);

    return (
        <div className="password-update-page">
            <Helmet>
                <title>{getPageMeta('passwordUpdate', language).title}</title>
                <meta
                    name="description"
                    content={getPageMeta('passwordUpdate', language).description}
                />
                {getPageMeta('passwordUpdate', language).noindex && (
                    <meta name="robots" content="noindex, follow" />
                )}
            </Helmet>
            <Loader loading={loading} />

            {/* Header */}
            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <Link to="/profile" className="back-btn">
                            <i className="icon" data-feather="arrow-left"></i>
                        </Link>
                        <h2>{t('updatePassword')}</h2>
                    </div>
                </div>
            </header>

            {/* Content Section */}
            <section>
                <div className="custom-container">
                    {/* Hero */}
                    <div className="pwd-hero">
                        <div className="pwd-hero-icon">
                            <FiLock />
                        </div>
                        <p className="pwd-hero-subtitle">{t('passwordUpdateSubtitle')}</p>
                    </div>

                    {error && (
                        <div className="alert-wrapper">
                            <Alert alert={'danger'}>{t(error)}</Alert>
                        </div>
                    )}

                    <form
                        onSubmit={handlePasswordUpdateSubmit}
                        className="auth-form pt-0 pwd-form-card"
                    >
                        {/* Old Password Field */}
                        <div className="form-group password-field">
                            <label htmlFor="inputoldpassword" className="form-label">
                                {t('currentPassword')}
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showOldPassword ? 'text' : 'password'}
                                    required
                                    className="form-control"
                                    id="inputoldpassword"
                                    placeholder={t('enterPassword')}
                                    value={oldPassword}
                                    onChange={(e) => setOldPassword(e.target.value)}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowOldPassword(!showOldPassword)}
                                    title={showOldPassword ? t('hidePassword') : t('showPassword')}
                                >
                                    {showOldPassword ? (
                                        <FiEyeOff className="eye-icon-text-field" />
                                    ) : (
                                        <FiEye className="eye-icon-text-field" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* New Password Field */}
                        <div className="form-group password-field">
                            <label htmlFor="inputnewpassword" className="form-label">
                                {t('newPassword')}
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    required
                                    className="form-control"
                                    id="inputnewpassword"
                                    placeholder={t('enterNewPassword')}
                                    value={newPassword}
                                    onChange={handleNewPasswordChange}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                    title={showNewPassword ? t('hidePassword') : t('showPassword')}
                                >
                                    {showNewPassword ? (
                                        <FiEyeOff className="eye-icon-text-field" />
                                    ) : (
                                        <FiEye className="eye-icon-text-field" />
                                    )}
                                </button>
                            </div>

                            {/* Password Strength Indicator */}
                            {newPassword && (
                                <div className="password-strength">
                                    <div className="strength-bar">
                                        <div
                                            className={`strength-fill strength-${passwordStrength}`}
                                        ></div>
                                    </div>
                                    <span className={`strength-text strength-${passwordStrength}`}>
                                        {passwordStrength === 'weak' && t('weak')}
                                        {passwordStrength === 'medium' && t('medium')}
                                        {passwordStrength === 'strong' && t('strong')}
                                    </span>
                                </div>
                            )}
                            {!newPassword && (
                                <p className="pwd-hint">{t('passwordRequirementHint')}</p>
                            )}
                        </div>

                        {/* Confirm Password Field */}
                        <div className="form-group password-field">
                            <label htmlFor="inputconfirmpassword" className="form-label">
                                {t('confirmPassword')}
                            </label>
                            <div className="password-input-wrapper">
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    required
                                    className="form-control"
                                    id="inputconfirmpassword"
                                    placeholder={t('confirmPassword')}
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                                <button
                                    type="button"
                                    className="password-toggle-btn"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    title={
                                        showConfirmPassword ? t('hidePassword') : t('showPassword')
                                    }
                                >
                                    {showConfirmPassword ? (
                                        <FiEyeOff className="eye-icon-text-field" />
                                    ) : (
                                        <FiEye className="eye-icon-text-field" />
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Password Match Indicator */}
                        {newPassword && confirmPassword && (
                            <div
                                className={`password-match-indicator ${newPassword === confirmPassword ? 'match' : 'no-match'}`}
                            >
                                <i
                                    className="icon"
                                    data-feather={
                                        newPassword === confirmPassword
                                            ? 'check-circle'
                                            : 'alert-circle'
                                    }
                                ></i>
                                <span>
                                    {newPassword === confirmPassword
                                        ? t('passwordsMatch')
                                        : t('passwordsDoNotMatch')}
                                </span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button
                            type="submit"
                            className="btn theme-btn w-100 mt-3"
                            disabled={loading}
                        >
                            <span>{loading ? t('updating') : t('updatePassword')}</span>
                        </button>

                        <div className="pwd-secure-note">
                            <FiShield />
                            <span>{t('passwordUpdateSubtitle')}</span>
                        </div>
                    </form>
                </div>

                {showToast && (
                    <SuccessToast
                        message={t('passwordUpdated')}
                        onClose={() => setShowToast(false)}
                    />
                )}
            </section>
        </div>
    );
}

export default PasswordUpdate;
